import React from 'react'
import PropTypes from 'prop-types'
import { NavLink } from 'react-router-dom'
import { Table as TableProvider, Select, Progress, Divider, Popover, Tooltip } from 'antd'
import { CheckCircleFilled, InfoCircleFilled, InfoCircleOutlined, MinusCircleOutlined } from '@ant-design/icons'
import numeral from 'numeral'
import moment from 'moment'
import { getTeamUrl } from 'Common'
import styles from './styles.css'

const Detail = ({ label, value }) => (
  <div style={{ display: 'flex' }}>
    <div style={{ width: 100, fontWeight: 500 }}>{label}</div>
    <div style={{ width: 100, textAlign: 'right' }}>{value}</div>
  </div>
)

const Table = props => {

  const {
    subValuations,
    fetching,
    pagination,
    onChangeTable,
    onChangeStatus,
    onClickNotes,
  } = props

  return (

    <TableProvider
      dataSource={subValuations}
      rowKey='id'
      loading={fetching}
      onChange={onChangeTable}
      pagination={{
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '30', '40', '50'],
        current: pagination.pageNumber,
        pageSize: pagination.pageSize,
        total: pagination.total,
      }}
    >

      <TableProvider.Column
        title='Reference'
        key='project_id'
        sorter
        render={(text, record) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <NavLink to={getTeamUrl(`sub_valuations/${record.id}`)}>
              <div style={{ fontSize: 15 }}>{record.reference}</div>
            </NavLink>
            {record.notes ? (
              <InfoCircleFilled
                style={{ fontSize: 16, color: 'orange', cursor: 'pointer' }}
                onClick={() => onClickNotes(record.id, record.notes)}
              />
            ) : (
              <InfoCircleOutlined
                style={{ fontSize: 16, color: '#999999', cursor: 'pointer' }}
                onClick={() => onClickNotes(record.id)}
              />
            )}
          </div>
        )}
      />

      <TableProvider.Column
        title='Subcontractor'
        key='subcontractor.name'
        sorter
        render={(text, record) => record.subcontractor.name}
      />

      <TableProvider.Column
        title='Project'
        key='project_id'
        sorter
        render={(text, record) => {
          const lastValuation = record.last_valuation || {};
          const project = record.project || {};

          const tooltipContent = (
            <div>
              {lastValuation.date && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, textDecoration: 'underline' }}>Last Client Valuation</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ marginRight: 20 }}>Date</span>
                    <span>{moment(lastValuation.date).format('Do MMM YYYY')}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ marginRight: 20 }}>Value</span>
                    <span>£{numeral(lastValuation.value || 0).format('0,0.00')}</span>
                  </div>
                </div>
              )}
              <div>
                <div style={{ fontWeight: 600, marginBottom: 8, textDecoration: 'underline' }}>Project Totals</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ marginRight: 20 }}>Value</span>
                  <span>£{numeral(project.value_total || 0).format('0,0.00')}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ marginRight: 20 }}>Completed</span>
                  <span>£{numeral(project.value_completed || 0).format('0,0.00')}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ marginRight: 20 }}>Remaining</span>
                  <span>£{numeral(project.value_remaining || 0).format('0,0.00')}</span>
                </div>
              </div>
            </div>
          );

          return (
            <Tooltip
              title={tooltipContent}
              placement="top"
              overlayInnerStyle={{ backgroundColor: 'white', color: 'black', minWidth: 250 }}
            >
              <div style={{ cursor: 'pointer' }}>
                <div>
                  <span style={{ fontSize: 15, fontWeight: 600 }}>{record.project.number}</span>
                  <span style={{ fontSize: 15, fontWeight: 400 }}>{` ${record.project.client_site.client.name}`}</span>
                </div>
                <div style={{ color: '#999999' }}>{record.project.client_site.name}</div>
              </div>
            </Tooltip>
          );
        }}
      />

      <TableProvider.Column
        title='Date'
        key='date'
        sorter
        render={(text, record) => moment(record.date).format('Do MMM YYYY')}
      />

      <TableProvider.Column
        title='To Date'
        key='to_date'
        sorter
        render={(text, record) => moment(record.to_date).format('Do MMM YYYY')}
      />

      <TableProvider.Column
        title='Owner'
        key='user.name'
        sorter
        render={(text, record) => record.user.full_name}
      />

      <TableProvider.Column
        title='Status'
        key='status'
        render={(text, record) => (
          <Select
            value={record.status}
            onChange={(value) => onChangeStatus(record.id, record.status, value)}
            style={{ width: 120 }}
            bordered={false}
          >
            <Select.Option value="Pending">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <MinusCircleOutlined style={{ fontSize: 15, color: '#999999', marginRight: 5 }} />
                <span>Pending</span>
              </div>
            </Select.Option>
            <Select.Option value="Approved">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleFilled style={{ fontSize: 15, color: 'orange', marginRight: 5 }} />
                <span>Approved</span>
              </div>
            </Select.Option>
            <Select.Option value="Paid">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleFilled style={{ fontSize: 15, color: 'limegreen', marginRight: 5 }} />
                <span>Paid</span>
              </div>
            </Select.Option>
          </Select>
        )}
      />

      <TableProvider.Column
        title='Complete'
        key='complete_total'
        sorter
        onHeaderCell={() => ({
          style: { textAlign: 'right' },
        })}
        onCell={() => ({
          style: { textAlign: 'right' },
        })}
        render={(text, record) => {
          const subProject = record.sub_project || {};
          const setValueTotal = subProject.set_value_total || 0;
          const setValueCompleted = subProject.set_value_completed || 0;
          const setValueRemaining = setValueTotal - setValueCompleted;

          const tooltipContent = (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 8, textDecoration: 'underline' }}>Subcontractor Totals</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ marginRight: 20 }}>Value</span>
                <span>£{numeral(setValueTotal).format('0,0.00')}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ marginRight: 20 }}>Completed</span>
                <span>£{numeral(setValueCompleted).format('0,0.00')}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ marginRight: 20 }}>Remaining</span>
                <span style={{ color: setValueRemaining < 0 ? 'red' : 'inherit' }}>£{numeral(setValueRemaining).format('0,0.00')}</span>
              </div>
            </div>
          );

          return (
            <Tooltip
              title={tooltipContent}
              placement="top"
              overlayInnerStyle={{ backgroundColor: 'white', color: 'black', minWidth: 250 }}
            >
              <div style={{ cursor: 'pointer' }}>
                <div style={{ fontSize: 15, fontWeight: 500 }}>
                  {`${record.complete_total < 0 ? '-' : ''}£${numeral(Math.abs(record.complete_total)).format('0,0.00')}`}
                </div>
                <Progress
                  percent={numeral(record.aggregate_perc).format('0,0')}
                  strokeColor={setValueRemaining < 0 ? 'red' : undefined}
                />
              </div>
            </Tooltip>
          );
        }}
      />

    </TableProvider>

  )

}

Detail.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
}

Table.propTypes = {
  subValuations: PropTypes.array.isRequired,
  fetching: PropTypes.bool.isRequired,
  pagination: PropTypes.object.isRequired,
  onChangeTable: PropTypes.func.isRequired,
  onChangeStatus: PropTypes.func.isRequired,
  onClickNotes: PropTypes.func.isRequired,
}

export default Table
